import { FunctionComponent, useMemo, useState } from "react";

import { isGUID } from "@bps/common-utils";
import { Pivot, PivotItem, useTheme } from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { FilterBar } from "@components/filter-bar/FilterBar";
import { Item } from "@components/filter-bar/FilterBar.types";
import { BpIdUsersSearchArgs } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { ValidationMessages } from "@libs/validation/validation-messages.enum";

type SearchMode = "Email" | "Identity";

interface BusinessUsersFilterProps {
  onFilter: (values: BpIdUsersSearchArgs) => void;
  onReset: () => void;
}

const baseValidator = new Validator<BpIdUsersSearchArgs>(v => ({
  email: v.string().email({ message: ValidationMessages.email }),
  mobile: v.string().phone({ message: "Numbers only" }),
  objectId: v.custom().func<"objectId">(value => {
    if (!value?.trim() || isGUID(value)) return value;
    return ValidationMessages.guid;
  })
}));

export const BusinessUsersFilter: FunctionComponent<BusinessUsersFilterProps> = ({
  onFilter,
  onReset
}) => {
  const theme = useTheme();
  const [mode, setMode] = useState<SearchMode>("Identity");

  const emailItems: Item<BpIdUsersSearchArgs>[] = useMemo(
    () => [
      {
        type: "searchBox",
        name: "email",
        props: { placeholder: "Email", removeSpecialCharacters: false },
        stickItem: true
      }
    ],
    []
  );

  const identityItems: Item<BpIdUsersSearchArgs>[] = useMemo(
    () => [
      {
        type: "searchBox",
        name: "objectId",
        props: { placeholder: "Object Id", removeSpecialCharacters: false },
        stickItem: true
      },
      {
        type: "searchBox",
        name: "givenNamePrefix",
        props: { placeholder: "Given Name", removeSpecialCharacters: false }
      },
      {
        type: "searchBox",
        name: "surnamePrefix",
        props: { placeholder: "Last Name", removeSpecialCharacters: false }
      },
      {
        type: "searchBox",
        name: "mobile",
        props: { placeholder: "Phone", removeSpecialCharacters: false }
      },
      {
        type: "searchBox",
        name: "userPrincipalNamePrefix",
        props: {
          placeholder: "User Principal Name",
          removeSpecialCharacters: false
        }
      }
    ],
    []
  );

  type ErrorMap = Partial<Record<keyof BpIdUsersSearchArgs, string>>;

  const baseValidate = (values: BpIdUsersSearchArgs): ErrorMap =>
    (baseValidator.validate(values) as ErrorMap) ?? {};

  const validate: (values: BpIdUsersSearchArgs) => ErrorMap = values => {
    const errors: ErrorMap = { ...baseValidate(values) };

    if (mode === "Email") {
      const hasEmail = !!values.email?.trim();

      if (!hasEmail) {
        errors.email = ValidationMessages.required ?? "Email is required";
      }

      delete errors.objectId;
      delete errors.givenNamePrefix;
      delete errors.surnamePrefix;
      delete errors.mobile;
      delete errors.userPrincipalNamePrefix;
    } else {
      const anyIdentity =
        !!values.objectId?.trim() ||
        !!values.givenNamePrefix?.trim() ||
        !!values.surnamePrefix?.trim() ||
        !!values.mobile?.trim() ||
        !!values.userPrincipalNamePrefix?.trim();

      if (!anyIdentity) {
        errors.objectId = "Enter at least one identity filter";
      }

      if (values.email?.trim()) {
        errors.email = "Email can’t be combined with identity filters";
      }
    }

    return errors;
  };

  const projectForMode = (values: BpIdUsersSearchArgs): BpIdUsersSearchArgs => {
    const {
      email,
      objectId,
      givenNamePrefix,
      surnamePrefix,
      mobile,
      userPrincipalNamePrefix
    } = values;

    return mode === "Email"
      ? { email }
      : {
          objectId,
          givenNamePrefix,
          surnamePrefix,
          mobile,
          userPrincipalNamePrefix
        };
  };

  const items = mode === "Email" ? emailItems : identityItems;

  return (
    <>
      <Pivot
        selectedKey={mode}
        onLinkClick={item => {
          const next = (item?.props.itemKey as SearchMode) ?? "Identity";
          setMode(next);
        }}
        styles={{
          link: { fontWeight: 600 },
          linkIsSelected: { color: theme.palette.themePrimary }
        }}
      >
        <PivotItem headerText="Identity" itemKey="Identity" />
        <PivotItem headerText="Email" itemKey="Email" />
      </Pivot>

      <FilterBar<BpIdUsersSearchArgs>
        key={mode}
        items={items}
        applyWithButton
        onFilter={(_actions, values) => onFilter(projectForMode({ ...values }))}
        onReset={actions => {
          actions.reset();
          onReset();
        }}
        validate={validate}
        maxItemWidth={190}
        styles={{
          actions: {
            ".ms-TooltipHost:last-child .ms-Button--icon:not(:disabled)": {
              background: theme.palette.neutralLight,
              i: {
                color: theme.palette.themePrimary,
                fontWeight: 600
              }
            }
          }
        }}
      />
    </>
  );
};
